"use client";
import { PIN_POSITIONS } from "./constants";

import { ImCross } from "react-icons/im";
import { MdDelete } from "react-icons/md";
import { FaEdit, FaCheck } from "react-icons/fa";
import Tooltip from "./Tooltip";

const TableBody = ({
  columns,
  data,
  editingRow,
  editedValues,
  handleCellChange,
  selectedRows,
  handleRowSelect,
  handleEditRow,
  handleSaveRow,
  handleCancelEdit,
  handleDeleteRow,
  columnWidths,
  showRowSelection,
  showActions,
  isLoading,
  loaderUI,
}) => {
  // Group columns by pin position
  const leftPinnedColumns = columns.filter(
    (col) => col.pin === PIN_POSITIONS.LEFT
  );
  const rightPinnedColumns = columns.filter(
    (col) => col.pin === PIN_POSITIONS.RIGHT
  );
  const unpinnedColumns = columns.filter(
    (col) => !col.pin || col.pin === PIN_POSITIONS.NONE
  );

  // Render cell content
  const renderCellContent = (row, column) => {
    const isEditing = editingRow === row?.id;
    const value = isEditing ? editedValues[column.key] : row[column.key];
    const cellValue = value !== undefined && value !== null ? value : "";

    // If cell has custom renderer, use it
    if (column.customCellRenderer) {
      return column.customCellRenderer(row);
    }

    // If cell is editable and row is being edited
    if (isEditing && column.isEditable) {
      return (
        <input
          type="text"
          value={cellValue}
          onChange={(e) => handleCellChange(column.key, e.target.value)}
          className="w-full p-1 border border-gray-300 rounded"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    // Default cell rendering with ellipsis and tooltip if needed
    const content = String(cellValue);
    const needsTooltip =
      column?.maxLength && content.length > column?.maxLength;

    return needsTooltip ? (
      <Tooltip text={content}>
        <span className="cursor-pointer">
          {content?.slice(0, column?.maxLength)}
          ...
        </span>
      </Tooltip>
    ) : (
      <div className="truncate max-w-xs">{content}</div>
    );
  };

  // Render cell
  const renderCell = (row, column, rowIndex) => {
    const isPinned = column.pin && column.pin !== PIN_POSITIONS.NONE;

    return (
      <td
        key={column.key}
        className={`px-4 py-2 whitespace-nowrap border-r ${
          isPinned
            ? `sticky ${
                column.pin === PIN_POSITIONS.LEFT ? "left-0" : "right-0"
              } z-10`
            : ""
        } ${
          editingRow === row?.id
            ? "bg-primary/5"
            : rowIndex % 2 === 0
            ? "bg-white"
            : "bg-gray-50"
        }`}
        style={{
          width: columnWidths[column.key],
          position: isPinned ? "sticky" : "relative",
          backgroundColor: isPinned
            ? rowIndex % 2 === 0
              ? "white"
              : "#f9fafb"
            : "",
        }}
      >
        {renderCellContent(row, column, rowIndex)}
      </td>
    );
  };

  if (isLoading) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={columns.length + (showRowSelection ? 2 : 1)}
            className="text-center py-8 "
          >
            {loaderUI()}
          </td>
        </tr>
      </tbody>
    );
  }
  if (!data || data.length === 0) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={columns.length + (showRowSelection ? 2 : 1)}
            className="text-center py-8 text-gray-500"
          >
            No data available
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {data.map((row, rowIndex) => {
        const isSelected = selectedRows?.some((r) => r.id === row.id);

        return (
          <tr
            key={row.id || rowIndex}
            className={`${isSelected ? "bg-primary/10" : ""} ${
              editingRow === row?.id ? "border-l-2 border-primary" : ""
            } hover:bg-gray-100`}
          >
            {showRowSelection && (
              <td
                className={`w-10 px-4 py-2 whitespace-nowrap ${
                  editingRow === row?.id
                    ? "bg-primary/5"
                    : rowIndex % 2 === 0
                    ? "bg-white"
                    : "bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleRowSelect(row)}
                  className="h-4 w-4 text-primary border-gray-300 rounded"
                  onClick={(e) => e.stopPropagation()}
                />
              </td>
            )}

            {leftPinnedColumns.map((column) =>
              renderCell(row, column, rowIndex)
            )}
            {unpinnedColumns.map((column) => renderCell(row, column, rowIndex))}
            {rightPinnedColumns.map((column) =>
              renderCell(row, column, rowIndex)
            )}

            {showActions && (
              <td
                className={`w-28 px-4 py-2 whitespace-nowrap sticky right-0 z-10 ${
                  editingRow === row?.id
                    ? "bg-primary/5"
                    : rowIndex % 2 === 0
                    ? "bg-white"
                    : "bg-gray-50"
                }`}
              >
                <div className="flex justify-start space-x-1">
                  {editingRow === row?.id ? (
                    <>
                      <button
                        onClick={() => handleSaveRow(row?.id)}
                        className="p-1 text-green-600 hover:text-green-800"
                        title="Save"
                      >
                        <FaCheck />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1 text-gray-600 hover:text-gray-800"
                        title="Cancel"
                      >
                        <ImCross className="text-sm" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEditRow(row?.id)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteRow(row?.id)}
                    className="p-1 text-red-600 hover:text-red-800"
                    title="Delete"
                  >
                    <MdDelete className="text-lg" />
                  </button>
                </div>
              </td>
            )}
          </tr>
        );
      })}
    </tbody>
  );
};

export default TableBody;
